import { SxProp, View, useDripsyTheme, styled, Pressable, FlatList } from 'dripsy';
import React, { useEffect, useState } from 'react';
import { AccountManagementStackRoutes, AccountManagementStackScreenProps } from '@/features/account-management/account.navigator';
import moment from 'moment-timezone';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniIconButton } from '@/design-system/components/buttons';
import { Row } from '@/design-system/components/layout';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { TextTitle, TextBody, TextSmall, H6, TextCaption } from '@/design-system/components/typography';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { formatAmountByDecimal } from '../../utils';
import { useTranslation } from 'react-i18next';
import { PageHeaderModalNative } from '../page/PageHeaderModalNative';
import { AccountTransactionIntentFilters } from '../../options';
import { RootStackRoutes } from 'types';
import { useNavigation } from '@react-navigation/native';

export interface AccountTransactionDetailProps {}

export const AccountTransactionDetailScreen: React.FC<
  AccountManagementStackScreenProps<AccountManagementStackRoutes.AccountTransactionDetail>
> = ({ navigation, route }) => {
  const { record } = route.params;
  const { theme } = useDripsyTheme();
  const onGoBack = () => {
    navigation.goBack();
  };
  const { t } = useTranslation('account-management');
  const { value: show, set: setShow } = useAsyncStorage<boolean>('showAmount');
  const hideShowBalance = (balance: string) => {
    return show ? '******' : balance;
  };
  const isNegative = (value: string) => {
    return (value && value.toString().startsWith('-')) || false;
  };

  const detailNavigation = useNavigation();

  return (
    <View sx={styles.contentContainer}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={onGoBack}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account-management::glossary.transaction-details')}
            </TextTitle>
          </View>
        </View>
      </PageHeaderModalNative>
      <PageItem sx={styles.card}>
        <View sx={styles.table}>
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$2' }}>
              <Icon name={record.asset.toLowerCase()} width={40} height={40} />
              <H6 bold sx={{ color: '$textPrimary' }}>
                {AccountTransactionIntentFilters(t).find((rec) => rec.value === record?.intent)?.label}
              </H6>
            </View>
            <View sx={{ width: '$10', height: '$10', justifyContent: 'center', zIndex: 1 }}>
              {!!record.xmetadata && (
                <UniIconButton
                  icon="info"
                  onPress={(e) => {
                    detailNavigation.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.Xmetadata,
                      params: { xmetadata: record.xmetadata }
                    });
                  }}
                />
              )}
            </View>
          </View>
          <View sx={{ gap: '$1' }}>
            <TextCaption semiBold sx={{ color: '$textSecondary' }}>
              {t('card-account-management::account-page.text--debit-credit')}
            </TextCaption>
            <H6 bold success={!isNegative(record.amount) && true} danger={isNegative(record.amount) && true}>
              {hideShowBalance(
                formatAmountByDecimal({
                  value: Math.abs(record.amount),
                  suffix: record.asset,
                  prefix: !isNegative(record.amount) ? '+' : '-',
                  decimals: 2
                })
              )}
            </H6>
          </View>
          {record?.fromAccountLabel && record?.fromAccountNumber && (
            <View sx={{ gap: '$3' }}>
              <View sx={{ flexDirection: 'row', gap: '$2', justifyContent: 'space-between' }}>
                <TextBody sx={{ color: '$textSecondary' }}>From</TextBody>
                <View sx={{ textAlign: 'right' }}>
                  <TextBody sx={{ color: '$textPrimary' }}>{record?.fromAccountLabel}</TextBody>
                  <TextBody sx={{ color: '$textPrimary' }}>{record?.fromAccountNumber}</TextBody>
                </View>
              </View>
            </View>
          )}
          <View sx={{ gap: '$3' }}>
            <View sx={{ flexDirection: 'row', gap: '$2', justifyContent: 'space-between' }}>
              <TextBody sx={{ color: '$textSecondary' }}>To</TextBody>
              <View sx={{ textAlign: 'right' }}>
                <TextBody sx={{ color: '$textPrimary' }}>{record?.toAccountLabel || record?.accountLabel || '-'}</TextBody>
                <TextBody sx={{ color: '$textPrimary' }}>{record?.toAccountNumber || record?.accountNumber || '-'}</TextBody>
              </View>
            </View>
          </View>
          <View sx={{ gap: '$3' }}>
            <View sx={{ flexDirection: 'row', gap: '$2', justifyContent: 'space-between' }}>
              <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::account-page.text--balance')}</TextBody>
              <TextBody semiBold sx={{ color: theme?.colors?.$secondary as string }}>
                {hideShowBalance(formatAmountByDecimal({ value: record.balance, suffix: record.asset, decimals: 2 }))}
              </TextBody>
            </View>
          </View>
          <View sx={{ gap: '$3' }}>
            <View sx={{ flexDirection: 'row', gap: '$2', justifyContent: 'space-between' }}>
              <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::card-details.text--date')}</TextBody>
              <TextBody sx={{ color: '$textPrimary' }}>{moment(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}</TextBody>
            </View>
          </View>
          <View sx={{ gap: '$3' }}>
            <View sx={{ flexDirection: 'row', gap: '$2', justifyContent: 'space-between' }}>
              <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::lock-card.text--ref-id')}</TextBody>
              <TextBody sx={{ color: '$textPrimary' }}>{record.intentRefId}</TextBody>
            </View>
          </View>
        </View>
      </PageItem>
    </View>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  card = 'card',
  table = 'table'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "overview assets"
      "transaction assets"
      "transaction assets"
      `,
    gridTemplateRows: 'repeat(3, auto)',
    height: '100%'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '$4',
    borderRadius: '$radius0',
    height: '100%'
  },
  table: {
    flexDirection: 'column',
    gap: '$4',
    px: '$5'
  }
};
